import React from "react";
import { Dialog } from "@mui/material";
import { eventDesignFont } from "../../eventDesignTheme";
import { WizardActionButton } from "./wizardShared";

// The website hides its own "Preview only" strip when this hash flag is present,
// because the dashboard bar below already shows that notice (EV-N8). Direct
// preview links keep the website strip. The flag goes in the hash because the
// dashboard accepts only the /event-preview path from the backend.
export const DASHBOARD_PREVIEW_HASH_FLAG = "embedded=dashboard";

export const toDashboardPreviewFrameUrl = (url: string): string =>
  `${url}${url.includes("#") ? "&" : "#"}${DASHBOARD_PREVIEW_HASH_FLAG}`;

// Bar and Close Preview button follow Claude Design (8) Create Event
// Screen.dc.html:952-965: the title row sits above a red text warning (no
// pill). The bar wraps on narrow screens so its items never overlap.
export default function EventPreviewDialog({ url, onClose }: { url: string | null; onClose: () => void }): React.ReactElement {
  return <Dialog fullScreen open={!!url} onClose={onClose} aria-labelledby="event-preview-title">
    <header
      className="flex shrink-0 flex-wrap items-center justify-between gap-[16px] border-b border-[#ECECEC] bg-[#F7F7F8] px-[16px] py-[14px] sm:px-[28px]"
      style={{ fontFamily: eventDesignFont }}
    >
      <div className="flex min-w-0 flex-col gap-[4px]">
        <div className="flex min-w-0 flex-wrap items-center gap-x-[12px] gap-y-[4px]">
          <h2 id="event-preview-title" className="m-0 whitespace-nowrap text-[12px] font-bold uppercase tracking-[.08em] text-[#727272]">Event Page Preview</h2>
          <p className="m-0 text-[12px] text-[#727272]">This is how the event will appear on your website.</p>
        </div>
        <div data-testid="event-preview-warning" className="flex min-w-0 items-center gap-[6px]">
          <svg viewBox="0 0 24 24" fill="none" stroke="#E24C4C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="h-[14px] w-[14px] shrink-0">
            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
          </svg>
          <span className="text-[13px] font-semibold text-[#E24C4C]">Preview only. Registration and payment are disabled.</span>
        </div>
      </div>
      <WizardActionButton
        tone="red"
        onClick={onClose}
        sx={{
          gap: "8px",
          fontSize: 13,
          minHeight: 0,
          px: "14px",
          py: "8px",
          lineHeight: "normal",
          borderRadius: "8px",
          border: "1px solid #F21B1B",
          backgroundColor: "#F21B1B",
          "&:hover": { backgroundColor: "#D01414", boxShadow: "none" },
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" style={{ width: 12, height: 12 }}>
          <path d="m6 6 12 12M18 6 6 18" />
        </svg>
        Close Preview
      </WizardActionButton>
    </header>
    {url ? <iframe src={toDashboardPreviewFrameUrl(url)} title="Event website preview" sandbox="allow-scripts allow-same-origin" referrerPolicy="no-referrer" className="min-h-0 w-full flex-1 border-0" /> : null}
  </Dialog>;
}
